import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "bootstrap/dist/css/bootstrap.min.css";

import { getApiBase, withBase } from "../config/api";
import axios from "axios";

import fallbackImage from "../assets/fallback.jpg";
import productDataFallback from "./ProductsData";

const resolveImageSrc = (src) => {
  if (!src) return fallbackImage;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith("/uploads") || src.startsWith("/api/uploads")) {
    return withBase(src);
  }
  return src;
};

const hasApiCollections = (apiData = []) =>
  Array.isArray(apiData) &&
  apiData.some(
    (category) =>
      Array.isArray(category?.subcollections) &&
      category.subcollections.some((collection) => Array.isArray(collection?.images) && collection.images.length > 0)
  );

export default function ProductsPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [expandedCollection, setExpandedCollection] = useState(null);

  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${getApiBase()}/api/products-all`);
        if (hasApiCollections(res.data)) {
          setProductData(res.data);
        } else {
          setProductData(productDataFallback);
        }
        setError(null);
      } catch (err) {
        setProductData(productDataFallback);
        setError(null);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleImgError = (e) => {
    e.target.src = fallbackImage;
  };

  const randomStyle = (i) => {
    const rotations = [-15, -8, -4, 5, 10, 15];
    return {
      rotate: rotations[i % rotations.length],
      x: Math.random() * 40 - 20,
      y: Math.random() * 40 - 20,
    };
  };

  const visibleCollections =
    activeTab === "all"
      ? productData.flatMap((cat) => cat.subcollections)
      : productData.find((cat) => cat.key === activeTab)?.subcollections || [];

  return (
    <div className="Padding-Top pb-5">
      <div className="container">
        <div className="col-12">
          <h1 className="custom-heading">Unveiling Timeless Collections</h1>
          <hr className="custom-divider" />
        </div>
        {/* Tabs */}
        <ul className="nav nav-tabs justify-content-center mb-5">
          {["all", "lehenga", "saree", "readymade"].map((tab) => (
            <li className="nav-item" key={tab}>
              <button
                className={`nav-link fw-bold ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            </li>
          ))}
        </ul>

        {/* Loading and Error states */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading collections...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger text-center py-5">
            <h4>Error</h4>
            <p>{error}</p>
          </div>
        )}

        {/* Piles */}
        {!loading && !error && (
          <div className="row g-4 justify-content-center">
            {visibleCollections.map((col) => (
              <div key={col.key} className="col-12 col-sm-6 col-md-4 col-lg-3 text-center mb-5">
                <div
                  className="position-relative d-flex justify-content-center flex-wrap"
                  style={{ minHeight: "280px" }}
                  onClick={() => setExpandedCollection(col)}
                >
                  {col.images.slice(0, 4).map((img, index) => (
                    <motion.div
                      key={index}
                      className="position-absolute"
                      style={{
                        width: "180px",
                        height: "240px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
                        cursor: "pointer",
                      }}
                      initial={randomStyle(index)}
                      whileHover={randomStyle(index)}
                      animate={randomStyle(index)}
                      transition={{ type: "spring", stiffness: 150, damping: 20 }}
                    >
                      <img
                        src={resolveImageSrc(img.src)}
                        alt={img.label}
                        onError={handleImgError}
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                    </motion.div>
                  ))}
                </div>
                
                {/* Collection Title */}
                <h5 className="mt-3 mb-2 fw-bold" style={{color: "#d70077"}}>{col.title}</h5>
                
                {/* --- NEW: Request Quote Button --- */}
                <a 
                  href="https://hs-india.com/wholesale-inquiry/" 
                  className="btn btn-sm btn-outline-dark"
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    borderColor: "#d70077", 
                    color: "#d70077",
                    borderRadius: "20px",
                    padding: "5px 20px",
                    fontWeight: "500"
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#d70077";
                    e.target.style.color = "#fff";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = "#d70077";
                  }}
                >
                  Request for Quote
                </a>

              </div>
            ))}
          </div>
        )}
        
        {/* Fullscreen Modal Slider */}
        <AnimatePresence>
          {expandedCollection && (
            <motion.div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-90"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ zIndex: 2000 }}
            >
              <div className="d-flex flex-column h-100">
                <h2 className="text-white text-center py-4 fw-bold">
                  {expandedCollection.title}
                </h2>

                <div className="flex-grow-1 d-flex align-items-center">
                  <Swiper
                    modules={[Navigation, Autoplay]}
                    navigation
                    autoplay={{
                      delay: 2500,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }}
                    spaceBetween={20}
                    slidesPerView={1}
                    breakpoints={{
                      768: { slidesPerView: 2 },
                      1024: { slidesPerView: 3 },
                      1400: { slidesPerView: 4 },
                      1600: { slidesPerView: 5 },
                    }}
                    className="h-100"
                  >
                    {expandedCollection.images.map((img, index) => (
                      <SwiperSlide key={index}>
                        <motion.img
                          src={resolveImageSrc(img.src)}
                          alt={img.label}
                          onError={handleImgError}
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.4 }}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "500px",
                            objectFit: "cover",
                            borderRadius: "12px",
                            boxShadow: "0 8px 25px rgba(0,0,0,0.4)",
                          }}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>

                <button
                  className="btn btn-light position-absolute top-0 end-0 m-4"
                  style={{ zIndex: 2100 }}
                  onClick={() => setExpandedCollection(null)}
                >
                  ✕ Close
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
